import React, { useState, useEffect, useCallback } from 'react';

const HISTORY_API = 'https://cdcfomf16k.execute-api.ap-south-1.amazonaws.com/default/Havells_tamp_alert_store';

const TAMPER_TYPES = {
  '1': { label:'Pump Tamper',     short:'Pump',         color:'#FF3B30', bg:'rgba(255,59,48,.08)',  icon:'🔧', glyph:'⚙️' },
  '2': { label:'Flow Sensor',     short:'Flow Sensor',  color:'#FF9F0A', bg:'rgba(255,159,10,.08)', icon:'💧', glyph:'〰️' },
  '3': { label:'Higher OutFlow',  short:'High Flow',    color:'#AF52DE', bg:'rgba(175,82,222,.08)', icon:'🌬️', glyph:'💨' },
  '4': { label:'Refreshments',    short:'Refreshments', color:'#34C759', bg:'rgba(52,199,89,.08)',  icon:'♻️', glyph:'🔄' },
};

const TAM_HISTORY_KEY = (mac) => `hoags_tam_history_${mac}`;

function getAllTamperEvents(devices) {
  const all = [];
  (devices || []).forEach(dev => {
    const mac = (dev.mac_id||dev.macid||'').replace(/[^a-fA-F0-9]/g,'').toLowerCase();
    if (!mac) return;
    try {
      const raw = JSON.parse(localStorage.getItem(TAM_HISTORY_KEY(mac)) || '[]');
      const latestPerCode = new Map();
      raw.forEach(ev => {
        const existing = latestPerCode.get(ev.tamCode);
        if (!existing || new Date(ev.detectedAt) >= new Date(existing.detectedAt)) {
          latestPerCode.set(ev.tamCode, ev);
        }
      });
      const deduped = Array.from(latestPerCode.values());
      localStorage.setItem(TAM_HISTORY_KEY(mac), JSON.stringify(deduped));
      deduped.forEach(ev => all.push({ ...ev, deviceName: dev.friendly_name||dev.friendlyname||mac, mac }));
    } catch {}
  });
  return all.sort((a,b) => new Date(b.detectedAt) - new Date(a.detectedAt));
}

function acknowledgeTamEvent(mac, id) {
  try {
    const hist = JSON.parse(localStorage.getItem(TAM_HISTORY_KEY(mac)) || '[]');
    const idx  = hist.findIndex(h => h.id === id);
    if (idx >= 0) {
      hist[idx].acknowledgedAt = new Date().toISOString();
      localStorage.setItem(TAM_HISTORY_KEY(mac), JSON.stringify(hist));
    }
  } catch {}
}

const STYLES = `
  @keyframes fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes pulse   { 0%,100%{opacity:1}50%{opacity:.4} }
  .tam-row  { transition:background .14s,box-shadow .14s; cursor:default; }
  .tam-row:hover  { background:#f8f9fc!important; }
  .tam-flt  { transition:all .16s; }
  .tam-flt:hover  { opacity:.8; }
`;

function KPICard({ label, value, color, icon, active, onClick, delay='0s' }) {
  return (
    <div onClick={onClick} style={{
      background: active ? color : '#fff',
      borderRadius:16, padding:'16px 18px', cursor: onClick?'pointer':'default',
      border: active ? `2px solid ${color}` : `1.5px solid ${color}22`,
      boxShadow: active ? `0 6px 20px ${color}33` : '0 2px 10px rgba(0,0,0,.05)',
      position:'relative', overflow:'hidden',
      animation:`fadeUp .35s ease ${delay} both`,
      transition:'all .2s',
    }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        background: active ? 'rgba(255,255,255,.4)' : color, borderRadius:'16px 16px 0 0' }}/>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div style={{ fontSize:11, fontWeight:700, color: active?'rgba(255,255,255,.8)':'#8E8E93',
          letterSpacing:.4, textTransform:'uppercase' }}>{label}</div>
        <span style={{ fontSize:18 }}>{icon}</span>
      </div>
      <div style={{ fontSize:30, fontWeight:900, color: active?'#fff':'#1c1c1e', letterSpacing:-.8, lineHeight:1 }}>
        {value}
      </div>
    </div>
  );
}

// ── Device Tamper History — fetches from cloud API ────────────────────────────
function DeviceTamperHistory({ devices }) {
  const [alerts,    setAlerts]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [filterMac, setFilterMac] = useState('all');
  const [filterTam, setFilterTam] = useState('all');
  const [expanded,  setExpanded]  = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ limit: 200 });
      if (filterMac !== 'all') params.set('mac', filterMac);
      if (filterTam !== 'all') params.set('tam_code', filterTam);
      const res  = await fetch(`${HISTORY_API}?${params}`);
      const data = await res.json();
      setAlerts(data.alerts || []);
    } catch (e) {
      setError('Failed to fetch history — check API');
    } finally {
      setLoading(false);
    }
  }, [filterMac, filterTam]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})
      + ', ' + d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
  };

  const getFriendlyName = (mac) => {
    const dev = (devices||[]).find(d =>
      (d.mac_id||d.macid||'').replace(/[^a-fA-F0-9]/g,'').toLowerCase() === mac.toLowerCase()
    );
    return dev?.friendly_name || dev?.friendlyname || mac.toUpperCase();
  };

  const grouped = alerts.reduce((acc, a) => {
    if (!acc[a.mac]) acc[a.mac] = [];
    acc[a.mac].push(a);
    return acc;
  }, {});

  const uniqueMacs = [...new Set(alerts.map(a => a.mac))];

  return (
    <div style={{ marginTop:8 }}>
      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        <select value={filterMac} onChange={e => setFilterMac(e.target.value)} style={{
          padding:'8px 12px', borderRadius:10, border:'1.5px solid rgba(0,0,0,.1)',
          fontSize:13, fontFamily:'inherit', background:'#fff', cursor:'pointer' }}>
          <option value="all">All Devices</option>
          {uniqueMacs.map(mac => (
            <option key={mac} value={mac}>{getFriendlyName(mac)} · {mac.toUpperCase()}</option>
          ))}
        </select>
        <select value={filterTam} onChange={e => setFilterTam(e.target.value)} style={{
          padding:'8px 12px', borderRadius:10, border:'1.5px solid rgba(0,0,0,.1)',
          fontSize:13, fontFamily:'inherit', background:'#fff', cursor:'pointer' }}>
          <option value="all">All Tamper Types</option>
          <option value="1">🔧 Pump Tamper</option>
          <option value="2">💧 Flow Sensor</option>
          <option value="3">🌬️ Higher OutFlow</option>
          <option value="4">♻️ Refreshments</option>
        </select>
        <button onClick={fetchHistory} disabled={loading} style={{
          padding:'8px 16px', borderRadius:10, border:'1.5px solid rgba(0,0,0,.1)',
          background:'#fff', color:'#007AFF', fontSize:13, fontWeight:600,
          cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:6 }}>
          <span style={loading ? {animation:'spin .7s linear infinite', display:'inline-block'} : {}}>↻</span>
          Refresh
        </button>
        <span style={{ fontSize:12, color:'#8E8E93', marginLeft:'auto' }}>
          {alerts.length} alert{alerts.length !== 1 ? 's' : ''} from cloud
        </span>
      </div>

      {error && (
        <div style={{ padding:'12px 16px', borderRadius:12, background:'rgba(255,59,48,.06)',
          border:'1px solid rgba(255,59,48,.15)', color:'#FF3B30', fontSize:13, marginBottom:16 }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:'48px', color:'#8E8E93' }}>
          <div style={{ width:28, height:28, borderRadius:'50%', border:'3px solid rgba(0,122,255,.12)',
            borderTopColor:'#007AFF', animation:'spin .7s linear infinite', margin:'0 auto 12px' }}/>
          <div style={{ fontSize:13 }}>Loading from cloud…</div>
        </div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign:'center', padding:'56px 24px', background:'#fff',
          borderRadius:20, border:'1px solid rgba(0,0,0,.06)' }}>
          <div style={{ fontSize:48, marginBottom:14 }}>📭</div>
          <div style={{ fontSize:16, fontWeight:700, color:'#1c1c1e', marginBottom:6 }}>No history found</div>
          <div style={{ fontSize:13, color:'#8E8E93' }}>No tamper alerts stored in cloud yet</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {Object.entries(grouped).map(([mac, macAlerts]) => {
            const name    = getFriendlyName(mac);
            const isOpen  = expanded === mac;
            const lastCfg = TAMPER_TYPES[macAlerts[0]?.tam_code] || TAMPER_TYPES['1'];
            return (
              <div key={mac} style={{ background:'#fff', borderRadius:16,
                border:'1px solid rgba(0,0,0,.06)', boxShadow:'0 2px 10px rgba(0,0,0,.05)',
                overflow:'hidden', animation:'fadeUp .3s ease both' }}>
                {/* Device header */}
                <div onClick={() => setExpanded(isOpen ? null : mac)} style={{
                  padding:'16px 20px', display:'flex', alignItems:'center', gap:14,
                  cursor:'pointer', background: isOpen ? '#fafafa' : '#fff',
                  borderBottom: isOpen ? '1px solid rgba(0,0,0,.06)' : 'none' }}>
                  <div style={{ width:44, height:44, borderRadius:13, flexShrink:0,
                    background:'linear-gradient(135deg,#DC2626,#EF4444)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color:'#fff', fontSize:16, fontWeight:800 }}>
                    {name[0]?.toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:'#1c1c1e' }}>{name}</div>
                    <div style={{ fontSize:11, color:'#8E8E93', fontFamily:'monospace' }}>{mac.toUpperCase()}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                    <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20,
                      background:'rgba(220,38,38,.08)', color:'#DC2626' }}>
                      {macAlerts.length} alert{macAlerts.length !== 1 ? 's' : ''}
                    </span>
                    <span style={{ fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20,
                      background:lastCfg.bg, color:lastCfg.color }}>
                      {lastCfg.icon} {lastCfg.short}
                    </span>
                    <span style={{ fontSize:16, color:'#8E8E93', transition:'transform .2s',
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', display:'inline-block' }}>
                      ▾
                    </span>
                  </div>
                </div>
                {/* Alert rows */}
                {isOpen && (
                  <div style={{ padding:'8px 12px 12px', display:'flex', flexDirection:'column', gap:8 }}>
                    {macAlerts.map((alert, idx) => {
                      const cfg = TAMPER_TYPES[alert.tam_code] || TAMPER_TYPES['1'];
                      return (
                        <div key={alert.timestamp} style={{
                          display:'flex', alignItems:'center', gap:12, padding:'12px 14px',
                          borderRadius:12, background:cfg.bg, border:`1px solid ${cfg.color}20`,
                          animation:`fadeUp .2s ease ${idx*.04}s both` }}>
                          <span style={{ fontSize:22, flexShrink:0 }}>{cfg.icon}</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:'#1c1c1e' }}>{alert.tam_label}</div>
                            <div style={{ fontSize:11, color:'#8E8E93', marginTop:2 }}>
                              🕐 {fmtDate(alert.timestamp)}
                              {alert.ts_device && (
                                <span style={{ marginLeft:8, color:'#aeaeb2' }}>Device: {alert.ts_device}</span>
                              )}
                            </div>
                          </div>
                          <span style={{ fontSize:9, fontWeight:800, padding:'3px 10px', borderRadius:20,
                            background:`${cfg.color}15`, color:cfg.color,
                            letterSpacing:.5, textTransform:'uppercase', flexShrink:0 }}>
                            TAM {alert.tam_code}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function TamperPage({ devices }) {
  const [tab,     setTab]     = useState('live');
  const [events,  setEvents]  = useState([]);
  const [filter,  setFilter]  = useState('all');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setEvents(getAllTamperEvents(devices));
    setTimeout(() => setLoading(false), 250);
  }, [devices]);

  useEffect(() => { refresh(); }, [refresh]);

  const total    = events.length;
  const pump     = events.filter(e=>e.tamCode==='1').length;
  const flow     = events.filter(e=>e.tamCode==='2').length;
  const high     = events.filter(e=>e.tamCode==='3').length;
  const refresh4 = events.filter(e=>e.tamCode==='4').length;
  const unacked  = events.filter(e=>!e.acknowledgedAt).length;
  const affected = new Set(events.map(e=>e.mac)).size;

  const filtered = events.filter(ev =>
    filter==='all' ? true :
    filter==='unacked' ? !ev.acknowledgedAt :
    ev.tamCode===filter
  );

  const handleAck = (mac, id) => { acknowledgeTamEvent(mac, id); refresh(); };

  const handleClearAll = () => {
    if (!window.confirm('Clear ALL tamper history? This cannot be undone.')) return;
    (devices||[]).forEach(dev => {
      const mac = (dev.mac_id||dev.macid||'').replace(/[^a-fA-F0-9]/g,'').toLowerCase();
      if (mac) localStorage.removeItem(TAM_HISTORY_KEY(mac));
    });
    refresh();
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})
      + ', ' + d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:true});
  };

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif", maxWidth:960 }}>
      <style>{STYLES}</style>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
        marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <img src="/havells-logo.png" alt="Havells" style={{ height:40, objectFit:'contain', marginBottom:10, display:'block' }}/>
          <h1 style={{ fontSize:26, fontWeight:800, color:'#1c1c1e', letterSpacing:-.5, margin:0, display:'flex', alignItems:'center', gap:10 }}>
            ⚠️ Tamper Alerts
          </h1>
          <p style={{ color:'#8E8E93', fontSize:13, marginTop:4, margin:'4px 0 0' }}>
            {tab === 'live'
              ? (total === 0 ? 'All clear — no tamper events recorded' : `${unacked} unacknowledged · ${affected} device${affected!==1?'s':''} affected`)
              : 'Full tamper history from cloud database'}
          </p>
        </div>
        {tab === 'live' && (
          <div style={{ display:'flex', gap:8, flexShrink:0 }}>
            <button onClick={refresh} disabled={loading} style={{
              display:'inline-flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:12,
              border:'1.5px solid rgba(0,0,0,.1)', background:'#fff', color:'#007AFF',
              fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
              <span style={loading ? {animation:'spin .7s linear infinite',display:'flex'} : {}}> ↻ </span>
              Refresh
            </button>
            {total > 0 && (
              <button onClick={handleClearAll} style={{
                padding:'9px 16px', borderRadius:12, border:'none',
                background:'rgba(255,59,48,.08)', color:'#FF3B30',
                fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                Clear All
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Tab switcher ── */}
      <div style={{ display:'flex', gap:4, background:'#f0f4f8', borderRadius:14,
        padding:4, marginBottom:24, width:'fit-content' }}>
        {[
          { id:'live',    label:'⚠️ Live Alerts' },
          { id:'history', label:'🗄️ Device Tamper History' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:'9px 20px', borderRadius:10, border:'none', fontSize:13,
            fontWeight: tab === t.id ? 700 : 500, cursor:'pointer', fontFamily:'inherit',
            background: tab === t.id ? '#fff' : 'transparent',
            color: tab === t.id ? '#1c1c1e' : '#8E8E93',
            boxShadow: tab === t.id ? '0 1px 8px rgba(0,0,0,.1)' : 'none',
            transition:'all .18s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── History tab ── */}
      {tab === 'history' && (
        <DeviceTamperHistory devices={devices} />
      )}

      {/* ── Live tab ── */}
      {tab === 'live' && (
        <div>
          {/* KPI Cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:12, marginBottom:22 }}>
            <KPICard label="Total Events"     value={total}    color="#1c1c1e" icon="📊" delay=".0s"/>
            <KPICard label="Pump Tampers"     value={pump}     color="#FF3B30" icon="🔧" active={filter==='1'} onClick={()=>setFilter(f=>f==='1'?'all':'1')} delay=".05s"/>
            <KPICard label="Flow Sensor"      value={flow}     color="#FF9F0A" icon="💧" active={filter==='2'} onClick={()=>setFilter(f=>f==='2'?'all':'2')} delay=".1s"/>
            <KPICard label="Higher OutFlow"   value={high}     color="#AF52DE" icon="🌬️" active={filter==='3'} onClick={()=>setFilter(f=>f==='3'?'all':'3')} delay=".15s"/>
            <KPICard label="Refreshments"     value={refresh4} color="#34C759" icon="♻️" active={filter==='4'} onClick={()=>setFilter(f=>f==='4'?'all':'4')} delay=".2s"/>
            <KPICard label="Unacknowledged"   value={unacked}  color="#FF3B30" icon="🔔" active={filter==='unacked'} onClick={()=>setFilter(f=>f==='unacked'?'all':'unacked')} delay=".25s"/>
            <KPICard label="Devices Affected" value={affected} color="#8E8E93" icon="📱" delay=".3s"/>
          </div>

          {/* Filter pills */}
          <div style={{ display:'flex', gap:6, marginBottom:18, flexWrap:'wrap' }}>
            {[
              { id:'all',     label:'All',            count:total    },
              { id:'unacked', label:'Unacknowledged', count:unacked  },
              { id:'1',       label:'Pump',           count:pump     },
              { id:'2',       label:'Flow Sensor',    count:flow     },
              { id:'3',       label:'Higher OutFlow', count:high     },
              { id:'4',       label:'Refreshments',   count:refresh4 },
            ].map(t => {
              const active = filter === t.id;
              return (
                <button key={t.id} onClick={()=>setFilter(t.id)} className="tam-flt" style={{
                  padding:'7px 14px', borderRadius:20, border:'none', fontSize:12, fontWeight:active?700:500,
                  cursor:'pointer', fontFamily:'inherit',
                  background: active?'#1c1c1e':'rgba(0,0,0,.05)',
                  color: active?'#fff':'#636366',
                  boxShadow: active?'0 2px 10px rgba(0,0,0,.2)':'none',
                }}>
                  {t.label}{t.count>0?` (${t.count})`:''}
                </button>
              );
            })}
          </div>

          {/* Events list */}
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {filtered.length === 0 ? (
              <div style={{ background:'#fff', borderRadius:20, padding:'56px 24px', textAlign:'center',
                border:'1px solid rgba(0,0,0,.06)', boxShadow:'0 2px 12px rgba(0,0,0,.05)' }}>
                <div style={{ fontSize:48, marginBottom:14 }}>✅</div>
                <div style={{ fontSize:16, fontWeight:700, color:'#1c1c1e', marginBottom:6 }}>All clear</div>
                <div style={{ fontSize:13, color:'#8E8E93' }}>
                  {filter==='all' ? 'No tamper events recorded yet'
                    : filter==='unacked' ? 'All events have been acknowledged'
                    : `No ${filter==='1'?'pump':filter==='2'?'flow sensor':filter==='3'?'high outflow':'refreshment'} events`}
                </div>
              </div>
            ) : filtered.map((ev, idx) => {
              const cfg   = TAMPER_TYPES[ev.tamCode] || TAMPER_TYPES['1'];
              const acked = !!ev.acknowledgedAt;
              return (
                <div key={`${ev.mac}-${ev.id}`} className="tam-row" style={{
                  background:'#fff', borderRadius:16, padding:'16px 20px',
                  border: acked ? '1px solid rgba(0,0,0,.06)' : `1.5px solid ${cfg.color}22`,
                  boxShadow: acked ? '0 1px 4px rgba(0,0,0,.04)' : `0 2px 14px ${cfg.color}18`,
                  display:'flex', alignItems:'center', gap:16, opacity: acked ? .6 : 1,
                  animation:`fadeUp .3s ease ${idx*.04}s both`,
                }}>
                  <div style={{ width:46, height:46, borderRadius:13, flexShrink:0,
                    background: acked ? 'rgba(0,0,0,.04)' : cfg.bg,
                    display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>
                    {cfg.icon}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:5, flexWrap:'wrap' }}>
                      <span style={{ fontSize:13, fontWeight:800, color:'#1c1c1e' }}>{cfg.label} Detected</span>
                      <span style={{ fontSize:9, fontWeight:800, padding:'2px 8px', borderRadius:20,
                        background: acked?'rgba(0,0,0,.06)':cfg.bg, color: acked?'#8E8E93':cfg.color,
                        letterSpacing:.6, textTransform:'uppercase' }}>{cfg.short}</span>
                      {acked && (
                        <span style={{ fontSize:9, fontWeight:700, color:'#34C759',
                          background:'rgba(52,199,89,.1)', padding:'2px 8px', borderRadius:20 }}>
                          ✓ Acknowledged
                        </span>
                      )}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                      <span style={{ fontSize:13, fontWeight:600, color:'#1c1c1e' }}>{ev.deviceName}</span>
                      <span style={{ fontSize:10, color:'#aeaeb2', fontFamily:'monospace' }}>{ev.mac?.toUpperCase()}</span>
                    </div>
                    <div style={{ display:'flex', gap:16, fontSize:11, color:'#8E8E93', flexWrap:'wrap' }}>
                      <span>🕐 {fmtDate(ev.detectedAt)}</span>
                      {acked && (
                        <span style={{ color:'#34C759', fontWeight:600 }}>
                          ✓ Ack: {fmtDate(ev.acknowledgedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                  {!acked ? (
                    <button onClick={() => handleAck(ev.mac, ev.id)} style={{
                      padding:'9px 18px', borderRadius:12, border:'none',
                      background:`linear-gradient(135deg,${cfg.color},${cfg.color}cc)`,
                      color:'#fff', fontSize:12, fontWeight:700,
                      cursor:'pointer', fontFamily:'inherit', flexShrink:0,
                      boxShadow:`0 3px 10px ${cfg.color}44` }}>
                      Acknowledge
                    </button>
                  ) : (
                    <div style={{ width:32, height:32, borderRadius:'50%', flexShrink:0,
                      background:'rgba(52,199,89,.1)',
                      display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12l5 5L19 7" stroke="#34C759" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
